import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartOptions,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import ChartDataLabels, { Context } from "chartjs-plugin-datalabels";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Chart } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

import { useRtl } from "@ryde/redux";
import {
  IBarGraphContent,
  IReportGridColumn,
  maxValueSuggestedMaxHelper,
  ReportPeriodGroupingEnum,
  truncateDecimals,
  useFormats,
  useTimezone,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import {
  downloadReportChartCanvasAsPng,
  formatReportChartDimensionPart,
  formatReportGroupingLabel,
  getBarBackgroundColors,
  getPieSliceColors,
  getReportChartBarDataValue,
  getReportChartLineDataLabelAnchor,
  getReportChartScrollMinHeight,
  isReportChartCartesianMode,
  isReportChartCompactMode,
  isReportChartLineMode,
  isReportChartPieMode,
  REPORT_CHART_AXIS_TITLE_STYLE,
  REPORT_CHART_BAR_THICKNESS_COMPACT,
  REPORT_CHART_BAR_THICKNESS_DEFAULT,
  REPORT_CHART_DATALABEL_FONT_SIZE_COMPACT,
  REPORT_CHART_DATALABEL_FONT_SIZE_DEFAULT,
  REPORT_CHART_GRID_COLOR,
  REPORT_CHART_LABEL_OUTSIDE_OFFSET,
  REPORT_CHART_LAYOUT_PADDING_RIGHT_COMPACT,
  REPORT_CHART_LAYOUT_PADDING_RIGHT_DEFAULT,
  REPORT_CHART_LINE_BORDER_WIDTH,
  REPORT_CHART_LINE_DATALABEL_BORDER_RADIUS,
  REPORT_CHART_LINE_DATALABEL_OFFSET,
  REPORT_CHART_LINE_DATALABEL_PADDING,
  REPORT_CHART_LINE_EXTRA_LAYOUT_PADDING,
  REPORT_CHART_LINE_POINT_RADIUS,
  REPORT_CHART_PIE_MIN_LABEL_PERCENT,
  REPORT_CHART_TICK_FONT_SIZE_COMPACT,
  REPORT_CHART_TICK_FONT_SIZE_DEFAULT,
  ReportChartDisplayMode,
  shouldPlaceDataLabelInsideBar,
  sortChartEntities,
  truncateLabel,
} from "./parts";
import {
  REPORT_CHART_BAR_BORDER_RADIUS,
  ReportChartBarCanvas,
  ReportChartBarContainer,
  ReportChartBarScrollArea,
} from "./report-chart.drawer.styled";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
);

export interface IReportChartBarContentRef {
  exportToImage: () => void;
}

interface IReportChartBarContentProps {
  content: IBarGraphContent;
  dimensionLabel: string;
  metricLabel: string;
  dimensionColumn?: IReportGridColumn;
  isReportGroupingDimension?: boolean;
  groupingDimensionColumns?: IReportGridColumn[];
  periodGrouping?: ReportPeriodGroupingEnum;
  displayMode: ReportChartDisplayMode;
  maxDisplay?: number;
  refreshSeed: number;
  displayGrid: boolean;
  hideLabels: boolean;
  exportFileName: string;
}

export const ReportChartBarContent = forwardRef<
  IReportChartBarContentRef,
  IReportChartBarContentProps
>(
  (
    {
      content,
      dimensionLabel,
      metricLabel,
      dimensionColumn,
      isReportGroupingDimension = false,
      groupingDimensionColumns = [],
      periodGrouping,
      displayMode,
      maxDisplay,
      refreshSeed,
      displayGrid,
      hideLabels,
      exportFileName,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { isRtl } = useRtl();
    const { timezone } = useTimezone();
    const pieChartRef = useRef<ChartJS<"pie">>(null);
    const cartesianChartRef = useRef<ChartJS<"bar" | "line">>(null);
    const { formatDateByGroupingPeriod } = useFormats();
    const isLineMode = isReportChartLineMode(displayMode);
    const isPieMode = isReportChartPieMode(displayMode);
    const isCartesianMode = isReportChartCartesianMode(displayMode);

    const formatDimensionValue = useCallback(
      (rawValue: string, truncate = true): string => {
        if (!rawValue) {
          return "";
        }

        if (isReportGroupingDimension && groupingDimensionColumns.length) {
          return formatReportGroupingLabel({
            compositeKey: rawValue,
            groupingColumns: groupingDimensionColumns,
            periodGrouping,
            timezone,
            t,
            formatDateByGroupingPeriod,
            truncate,
          });
        }

        const formatted = formatReportChartDimensionPart({
          rawValue,
          column: dimensionColumn,
          periodGrouping,
          timezone,
          t,
          formatDateByGroupingPeriod,
        });

        return truncate ? truncateLabel({ text: formatted }) : formatted;
      },
      [
        dimensionColumn,
        formatDateByGroupingPeriod,
        groupingDimensionColumns,
        isReportGroupingDimension,
        periodGrouping,
        t,
        timezone,
      ],
    );

    const formatMetricValue = useCallback((value: number): string => {
      return String(truncateDecimals(value));
    }, []);

    const sortedEntities = useMemo(
      () => sortChartEntities({ content, maxDisplay }),
      [content, maxDisplay],
    );

    const chartPoints = useMemo(
      () =>
        sortedEntities.map(({ subLabel, value }) => {
          const raw = String(subLabel);
          return {
            raw,
            label: formatDimensionValue(raw, true),
            tooltipLabel: formatDimensionValue(raw, false),
            value,
          };
        }),
      [formatDimensionValue, sortedEntities],
    );

    const labels = useMemo(
      () => chartPoints.map(point => point.label),
      [chartPoints],
    );

    const values = useMemo(
      () => chartPoints.map(point => point.value),
      [chartPoints],
    );

    useImperativeHandle(
      ref,
      () => ({
        exportToImage: () => {
          const canvas = (
            isPieMode ? pieChartRef.current : cartesianChartRef.current
          )?.canvas;
          if (!canvas) {
            return;
          }

          downloadReportChartCanvasAsPng({
            canvas,
            fileName: exportFileName,
          });
        },
      }),
      [exportFileName, isPieMode],
    );

    if (!labels.length) {
      return null;
    }

    const barCount = labels.length;
    const isCompactMode =
      isCartesianMode && isReportChartCompactMode({ barCount });
    const chartMinHeight = isCompactMode
      ? getReportChartScrollMinHeight({ barCount })
      : undefined;
    const barThickness = isCompactMode
      ? REPORT_CHART_BAR_THICKNESS_COMPACT
      : REPORT_CHART_BAR_THICKNESS_DEFAULT;
    const maxValue = Math.max(...values, 0);
    const solidBarColor = rydeColors.reportChartBar;
    let barColors: string | string[] = solidBarColor;
    if (
      !isLineMode &&
      (displayMode === ReportChartDisplayMode.colorByValue ||
        displayMode === ReportChartDisplayMode.differentColors)
    ) {
      barColors = getBarBackgroundColors({
        displayMode,
        values,
        maxValue,
        refreshSeed,
      });
    }
    const pieSliceColors = getPieSliceColors({
      sliceCount: values.length,
      refreshSeed,
    });
    const valuesTotal = values.reduce((sum, value) => sum + value, 0);

    const pieOptions: ChartOptions<"pie"> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: isRtl ? "left" : "right",
          labels: {
            boxWidth: 12,
            font: { size: 11 },
          },
        },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<"pie">[]) => {
              const index = items[0]?.dataIndex;
              const point = chartPoints[index];
              return point?.tooltipLabel ?? point?.label ?? "";
            },
            label: (item: TooltipItem<"pie">) => {
              const value = Number(item.raw);
              const share =
                valuesTotal > 0
                  ? ((value / valuesTotal) * 100).toFixed(1)
                  : "0";
              return `${metricLabel}: ${formatMetricValue(value)} (${share}%)`;
            },
          },
        },
        datalabels: {
          display: (context: Context): boolean =>
            !hideLabels && Number(context.dataset.data[context.dataIndex]) > 0,
          formatter: (value: number) => {
            const share =
              valuesTotal > 0 ? (Number(value) / valuesTotal) * 100 : 0;
            return share >= REPORT_CHART_PIE_MIN_LABEL_PERCENT
              ? `${share.toFixed(0)}%`
              : "";
          },
          color: rydeColors.white,
          font: { size: 11, weight: 600 },
        },
      },
    };

    const cartesianOptions: ChartOptions<"bar" | "line"> = {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          right: (() => {
            if (isLineMode) {
              return (
                REPORT_CHART_LAYOUT_PADDING_RIGHT_DEFAULT +
                REPORT_CHART_LINE_EXTRA_LAYOUT_PADDING
              );
            }
            if (isCompactMode) {
              return REPORT_CHART_LAYOUT_PADDING_RIGHT_COMPACT;
            }
            return REPORT_CHART_LAYOUT_PADDING_RIGHT_DEFAULT;
          })(),
          left: 4,
        },
      },
      scales: {
        x: {
          reverse: isRtl,
          beginAtZero: true,
          suggestedMax: maxValueSuggestedMaxHelper(
            values.length ? Math.max(...values) : 0,
          ),
          title: {
            display: true,
            text: metricLabel,
            ...REPORT_CHART_AXIS_TITLE_STYLE,
          },
          grid: {
            display: displayGrid,
            color: REPORT_CHART_GRID_COLOR,
            lineWidth: 1,
          },
        },
        y: {
          position: isRtl ? "right" : "left",
          title: {
            display: true,
            text: dimensionLabel,
            ...REPORT_CHART_AXIS_TITLE_STYLE,
          },
          grid: {
            display: displayGrid,
            color: REPORT_CHART_GRID_COLOR,
            lineWidth: 1,
          },
          ticks: {
            autoSkip: false,
            font: {
              size: isCompactMode
                ? REPORT_CHART_TICK_FONT_SIZE_COMPACT
                : REPORT_CHART_TICK_FONT_SIZE_DEFAULT,
            },
          },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items: TooltipItem<"bar" | "line">[]) => {
              const index = items[0]?.dataIndex;
              const point = chartPoints[index];
              return point?.tooltipLabel ?? point?.label ?? "";
            },
            label: (item: TooltipItem<"bar" | "line">) => {
              const value = Number(item.raw);
              return `${metricLabel}: ${formatMetricValue(value)}`;
            },
          },
        },
        datalabels: {
          clip: false,
          display: (context: Context): boolean =>
            !hideLabels && getReportChartBarDataValue({ context }) > 0,
          formatter: (value: number) => formatMetricValue(Number(value)),
          font: {
            size: isCompactMode
              ? REPORT_CHART_DATALABEL_FONT_SIZE_COMPACT
              : REPORT_CHART_DATALABEL_FONT_SIZE_DEFAULT,
            weight: 600,
          },
          anchor: (context: Context): "center" | "end" | "start" => {
            if (isLineMode) {
              return getReportChartLineDataLabelAnchor({ isRtl });
            }

            const value = getReportChartBarDataValue({ context });
            if (
              shouldPlaceDataLabelInsideBar({ value, maxValue, isCompactMode })
            ) {
              return "center";
            }
            return isRtl ? "start" : "end";
          },
          align: (context: Context): "center" | "end" | "start" => {
            if (isLineMode) {
              return getReportChartLineDataLabelAnchor({ isRtl });
            }

            const value = getReportChartBarDataValue({ context });
            if (
              shouldPlaceDataLabelInsideBar({ value, maxValue, isCompactMode })
            ) {
              return "center";
            }
            return isRtl ? "start" : "end";
          },
          color: (context: Context): string => {
            if (isLineMode) {
              return rydeColors.reportChartAxisTitle;
            }

            const value = getReportChartBarDataValue({ context });
            return shouldPlaceDataLabelInsideBar({
              value,
              maxValue,
              isCompactMode,
            })
              ? rydeColors.white
              : rydeColors.reportChartAxisTitle;
          },
          backgroundColor: (context: Context): string | null => {
            if (!isLineMode) {
              return null;
            }

            return getReportChartBarDataValue({ context }) > 0
              ? rydeColors.white
              : null;
          },
          borderRadius: isLineMode
            ? REPORT_CHART_LINE_DATALABEL_BORDER_RADIUS
            : 0,
          padding: isLineMode ? REPORT_CHART_LINE_DATALABEL_PADDING : 0,
          offset: (context: Context): number => {
            if (isLineMode) {
              return REPORT_CHART_LINE_DATALABEL_OFFSET;
            }

            const value = getReportChartBarDataValue({ context });
            return shouldPlaceDataLabelInsideBar({
              value,
              maxValue,
              isCompactMode,
            })
              ? 0
              : REPORT_CHART_LABEL_OUTSIDE_OFFSET;
          },
        },
      },
    };

    const barEndRadius = isRtl
      ? {
          topLeft: REPORT_CHART_BAR_BORDER_RADIUS,
          bottomLeft: REPORT_CHART_BAR_BORDER_RADIUS,
        }
      : {
          topRight: REPORT_CHART_BAR_BORDER_RADIUS,
          bottomRight: REPORT_CHART_BAR_BORDER_RADIUS,
        };

    const pieChartData = {
      labels,
      datasets: [
        {
          data: values,
          backgroundColor: pieSliceColors,
          borderColor: rydeColors.white,
          borderWidth: 1,
        },
      ],
    };

    const cartesianChartData = isLineMode
      ? {
          labels,
          datasets: [
            {
              type: "line" as const,
              data: values,
              borderColor: solidBarColor,
              backgroundColor: solidBarColor,
              pointBackgroundColor: solidBarColor,
              pointBorderColor: solidBarColor,
              pointRadius: REPORT_CHART_LINE_POINT_RADIUS,
              pointHoverRadius: REPORT_CHART_LINE_POINT_RADIUS + 1,
              borderWidth: REPORT_CHART_LINE_BORDER_WIDTH,
              fill: false,
              tension: 0,
            },
          ],
        }
      : {
          labels,
          datasets: [
            {
              type: "bar" as const,
              data: values,
              backgroundColor: barColors,
              borderColor: barColors,
              borderWidth: 0,
              borderRadius: barEndRadius,
              borderSkipped: false,
              barThickness,
            },
          ],
        };

    const chart = isPieMode ? (
      <Chart
        ref={pieChartRef}
        type="pie"
        data={pieChartData}
        options={pieOptions}
      />
    ) : (
      <Chart
        ref={cartesianChartRef}
        type={isLineMode ? "line" : "bar"}
        data={cartesianChartData}
        options={cartesianOptions}
      />
    );

    if (isCompactMode) {
      return (
        <ReportChartBarScrollArea>
          <ReportChartBarCanvas $minHeight={chartMinHeight}>
            {chart}
          </ReportChartBarCanvas>
        </ReportChartBarScrollArea>
      );
    }

    return <ReportChartBarContainer>{chart}</ReportChartBarContainer>;
  },
);

ReportChartBarContent.displayName = "ReportChartBarContent";
